import { nextAvailableDeliveryDate } from '@/utils/Date';
import type { Stock, StockLocation, TransformedStockLocation, SubAvailabilityDate } from '@/types/stock';
import type { Variant, PurchaseConstraints } from '@/types/products';
import type { BrandConfig } from '@/config/brand.config';

const transformStockLocation = ({
  location,
  centralStorageId,
  stock,
  includeCentralStorageSubDates,
}: {
  location: StockLocation;
  centralStorageId: string | undefined;
  stock: Stock;
  includeCentralStorageSubDates: boolean;
}): TransformedStockLocation => {
  const { $i18n } = useNuxtApp();

  const {
    availabilityDateISO,
    locationId,
    quantity,
    orderMultiple,
    subAvailabilities,
    availableToDays,
    availableFromDays,
    stocked,
  } = location;
  const isCentralStorageStock = locationId === centralStorageId;
  const supplyGroupNormalized = stock.stockType ? stock.stockType.toUpperCase() : '';

  const inStockText = isCentralStorageStock ? $i18n.t('stock.in_central_stock') : $i18n.t('stock.in_store_stock');
  let outOfStockText = $i18n.t('stock.out_of_stock');

  if (isCentralStorageStock) {
    switch (supplyGroupNormalized) {
      case STOCK_TYPE.TRANSIT_PLUS:
        outOfStockText = $i18n.t('stock.delivery_in_x_days', {
          fromDays: availableFromDays ?? '10',
          toDays: availableToDays ?? '15',
        });
        break;
      case STOCK_TYPE.TRANSIT:
        outOfStockText = $i18n.t('stock.order_product');
        break;
      case STOCK_TYPE.VIRTUAL:
        outOfStockText = $i18n.t('stock.delivery_in_x_days', {
          fromDays: availableFromDays ?? '5',
          toDays: availableToDays ?? '10',
        });
        break;
    }
  }

  return {
    unit: ' st ',
    stocked,
    quantity,
    locationId,
    inStockText,
    supplyGroup: supplyGroupNormalized,
    availability: undefined,
    orderMultiple,
    outOfStockText,
    availabilityDate: isCentralStorageStock ? availabilityDateISO : undefined,
    subAvailabilityDates: isCentralStorageStock && includeCentralStorageSubDates ? subAvailabilities : [],
  };
};

const transformStockLocationWithAvailability = ({
  location,
  requestedDeliveryDate,
  discontinued,
}: {
  location: StockLocation;
  requestedDeliveryDate: Date;
  discontinued?: boolean;
}): TransformedStockLocation => {
  const { $i18n } = useNuxtApp();

  const {
    availabilityDateISO,
    locationId,
    quantity,
    orderMultiple,
    subAvailabilities,
    availableToDays,
    availableFromDays,
    stocked,
    availability,
  } = location;
  let outOfStockText = $i18n.t('stock.out_of_stock');

  const inStockText = locationId === 'VIRTUAL' ? $i18n.t('stock.in_stock') : $i18n.t('stock.in_store_stock');
  const outOfStockTexts = {
    gray: $i18n.t('stock.order_product'),
    yellow: $i18n.t('stock.delivery_in_x_days', {
      fromDays: availableFromDays,
      toDays: availableToDays,
    }),
  };
  if (availability === 'gray' && !stocked) outOfStockText = outOfStockTexts.gray;
  if (quantity === 0 && discontinued) outOfStockText = $i18n.t('stock.not_stocked_in_store');
  else if (availability === 'yellow') outOfStockText = outOfStockTexts.yellow;

  /**
   * In order to have the same format as Dahl on subAvailabilityDates,
   * we need to add a new availability with the requested availabilityDate
   * and the available quantity
   */
  let subAvailabilityDates: SubAvailabilityDate[] = [];
  if (subAvailabilities?.length && requestedDeliveryDate) {
    subAvailabilityDates = [
      {
        ...subAvailabilities[0],
        quantity: Number(subAvailabilities[0].quantity) - quantity,
      },
    ];
    if (quantity !== 0) {
      subAvailabilityDates.splice(0, 0, {
        date: new Date(requestedDeliveryDate).toISOString(),
        quantity,
      });
    }
  }
  return {
    unit: ' st ',
    stocked,
    quantity,
    locationId,
    inStockText,
    supplyGroup: '',
    availability,
    orderMultiple,
    outOfStockText,
    availabilityDate: availabilityDateISO,
    subAvailabilityDates,
  };
};

export const transformStock = ({
  stock,
  centralStorageId,
  includeCentralStorageSubDates = false,
  brandConfig,
  requestedDeliveryDate,
  discontinued,
}: {
  stock?: Stock;
  centralStorageId?: string | undefined;
  includeCentralStorageSubDates?: boolean;
  brandConfig?: BrandConfig;
  requestedDeliveryDate?: any;
  discontinued?: boolean;
}): TransformedStockLocation[] => {
  // StockIndicator component needs an object with specific values to show failed stock message
  if (!stock?.locations?.length) return [{ quantity: -1, inStockText: '-' }];
  return stock.locations.map((location) => {
    if (brandConfig?.additions.useStockAvailabilityColors)
      return transformStockLocationWithAvailability({ location, requestedDeliveryDate, discontinued });
    return transformStockLocation({ location, includeCentralStorageSubDates, centralStorageId, stock });
  });
};

export const STOCK_TYPE = {
  WAREHOUSE: 'LAG',
  TRANSIT: 'TRA',
  TRANSIT_PLUS: 'TRA_PLUS',
  VIRTUAL: 'VIR',
};

export const PURCHASE_CONSTRAINTS = {
  ONLY_STORE: 'onlyStore',
  MUST_BE_STOCKED_AT: 'mustBeInStockedAt',
};

export function getRequestedDeliveryDateString(date?: string) {
  const requestedDate = date || nextAvailableDeliveryDate(new Date()).toISOString();
  const dateWithoutTimeStamp = requestedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dateString = dateWithoutTimeStamp?.[0];
  return dateString;
}

export function getOrderMultiple(selectedDeliveryId: string, stock: Stock) {
  if (!stock || !stock.locations?.length) return 1;

  const location =
    stock.locations.length === 1
      ? stock.locations[0]
      : stock.locations.find((location) => location.locationId === selectedDeliveryId);
  return location?.orderMultiple || 1;
}

export function getAssembledOrderDate(stockItems: StockLocation[]) {
  let backInStockDate: string | undefined;
  stockItems?.forEach(({ availabilityDateISO }) => {
    if (!availabilityDateISO) {
      backInStockDate = undefined;
      return;
    }
    if (!backInStockDate || availabilityDateISO > backInStockDate) backInStockDate = availabilityDateISO;
  });

  return backInStockDate;
}

export function isPurchasableOnline(
  variant: Variant | { purchaseConstraints: PurchaseConstraints[]; stock?: Stock; stockDisplayList?: StockLocation[] },
  locationId: string,
) {
  const availability = {
    store: true,
    online: true,
  };

  if (variant?.purchaseConstraints?.includes(PURCHASE_CONSTRAINTS.ONLY_STORE)) {
    availability.online = false;
  }

  if (variant?.purchaseConstraints?.includes(PURCHASE_CONSTRAINTS.MUST_BE_STOCKED_AT)) {
    const locations = variant.stock?.locations || variant.stockDisplayList || [];
    const location = locations.find((loc) => loc.locationId === locationId);
    availability.store = location?.stocked || false;
  }

  return availability;
}

export function isVariantBuyable(variant: Variant) {
  if (!variant) return false;
  if (!variant.discontinued) return true;
  return !!variant.stock?.locations?.some((location) => location.quantity > 0);
}
